/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { Store } from "store";
import { Timestamp } from "@google-cloud/firestore";

import { CommentFilterType } from "modules/streaming";

import { EventInfo } from "utility";

import { StreamingActionHeader } from "./StreamingActionHeader";
import { Box } from "@material-ui/core";
import { StreamingAnnouncement } from "./StreamingAnnouncement";
import StreamingCommentV2 from "./StreamingCommentV2";
import { useEmoBalance } from "hooks/paw/useEmoBalance";
import StreamingLiveCommerceV2 from "./StreamingLiveCommerceV2";
import { StreamingSelectGiftV2 } from "./StreamingSelectGiftV2";
import { StreamingSendCommentV2 } from "./StreamingSendCommentV2";
import { StreamingCommentHidden } from "./StreamingCommentHidden";
import { ActionAreaType } from "./useActionAreaType";
import { useSendComment } from "./useSendComment";
import { isPopOut } from "./enableCommentPopOut";
import type { PlainVideoPlayer } from "./videojsPlayer";
import { StreamingLiveEventV2 } from "./StreamingLiveEventV2";

type Props = {
  event: EventInfo;
  player: PlainVideoPlayer | null;
  eventStart: Timestamp;
  currentVideoId: string;
  THEOPlayerElementHeight: number;
  isHiddenStreamingComment: boolean;
  isLiveCommerceEnabled: boolean;
  setWatchVideo: (vid: string) => void;
  isOpenAnnouncement: boolean;
  handleToggleAnnouncement: () => void;
  isEditingAnnouncement: boolean;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  streamingAnnouncement: string;
  setStreamingAnnouncement: (announcement: string) => void;
  isOpenLiveEvent: boolean;
  handleToggleLiveEvent: () => void;
  actionAreaType: ActionAreaType;
  selectedCommentFilter: CommentFilterType;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
  handleCloseActionArea: () => void;
  hasDvrStartTime: boolean;
  isLiveEventEnabled: boolean;
};

/**
 * UserActionArea
 * comment, gift, (later: goods sales, quiz, vote)
 * @param props
 */
const StreamingActionAreaV2: React.FC<Props> = (props) => {
  // ----- Hooks -----
  const { paidValue: emoBalance } = useEmoBalance();
  const {
    commentInputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  } = useSendComment({
    currentVideoId: props.currentVideoId,
  });

  // ----- Redux State -----
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const user = useSelector((state: Store) => state.auth.user);
  const streamingType = useSelector(
    (state: Store) => state.streaming.streamingType
  );

  const streamingAnnouncement = (
    <StreamingAnnouncement
      eventId={props.event.eventId}
      currentVideoId={props.currentVideoId}
      streamingType={streamingType}
      isOpenAnnouncement={props.isOpenAnnouncement}
      isEditingAnnouncement={props.isEditingAnnouncement}
      setIsEditingAnnouncement={props.setIsEditingAnnouncement}
      streamingAnnouncement={props.streamingAnnouncement}
      setStreamingAnnouncement={props.setStreamingAnnouncement}
    />
  );

  const streamingCommentV2 = (
    <StreamingCommentV2
      player={props.player}
      eventStart={props.eventStart}
      streamingType={streamingType}
      eventInfo={props.event}
      currentVideoId={props.currentVideoId}
      selectedCommentFilter={props.selectedCommentFilter}
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      hasDvrStartTime={props.hasDvrStartTime}
    />
  );

  const streamingSendCommentV2 = streamingType === "Live" && user.uid && (
    <StreamingSendCommentV2
      isDarkModeEnabled={isDarkModeEnabled}
      isOpenGiftDrawer={isOpenGiftDrawer}
      isSelectedGift={selectedGiftItem !== null}
      canPurchaseGiftItem={canPurchaseGiftItem}
      userIcon={iconUrls?.["32"]}
      userName={user.displayName ?? ""}
      inputRef={commentInputRef}
      comment={comment}
      canSendComment={canSendComment}
      giftItems={giftItems}
      onClickSendComment={handleSendComment}
      setComment={handleSetComment}
      onClickOpenGiftDrawer={handleOpenGiftDrawer}
      onClickOpenPawChargeModal={handleOpenPawChargeModal}
    />
  );

  const streamingActionHeader = (
    <StreamingActionHeader
      isDarkModeEnabled={isDarkModeEnabled}
      handleChangeCommentFilter={props.handleChangeCommentFilter}
      handleToggleAnnouncement={props.handleToggleAnnouncement}
      handleCloseActionArea={props.handleCloseActionArea}
      selectedCommentFilter={props.selectedCommentFilter}
      showAnnouncement={isAdmin || props.streamingAnnouncement !== ""}
      isLiveEventEnabled={props.isLiveEventEnabled}
      handleToggleLiveEvent={props.handleToggleLiveEvent}
    />
  );
  const streamingActionHeaderNoClose = (
    <StreamingActionHeader
      isDarkModeEnabled={isDarkModeEnabled}
      handleChangeCommentFilter={props.handleChangeCommentFilter}
      handleToggleAnnouncement={props.handleToggleAnnouncement}
      selectedCommentFilter={props.selectedCommentFilter}
      showAnnouncement={isAdmin || props.streamingAnnouncement !== ""}
      isLiveEventEnabled={props.isLiveEventEnabled}
      handleToggleLiveEvent={props.handleToggleLiveEvent}
    />
  );

  const streamingSelectGiftV2 = isOpenGiftDrawer && giftItems.length > 0 && (
    <StreamingSelectGiftV2
      isDarkModeEnabled={isDarkModeEnabled}
      canPurchaseGiftItem={canPurchaseGiftItem}
      iconUrls={iconUrls?.["32"]}
      userName={user.displayName ?? ""}
      comment={comment}
      emoBalance={emoBalance}
      giftItems={giftItems}
      selectedGiftItem={selectedGiftItem}
      canSendComment={canSendComment}
      onClickSendComment={handleSendComment}
      onClickCloseGiftDrawer={handleCloseGiftDrawer}
      onClickSelectedGift={handleClickSelectedGift}
      onClickOpenPawChargeModal={handleOpenPawChargeModal}
    />
  );

  if (isPopOut()) {
    return (
      <StreamingActionAreaLayout
        className={"streaming_action_area_height"}
        header={streamingActionHeader}
      >
        {streamingAnnouncement}
        {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

        <Box flex={"1 1 auto"}>{streamingCommentV2}</Box>
        {streamingSelectGiftV2}
        {streamingSendCommentV2}
      </StreamingActionAreaLayout>
    );
  }

  return (
    <React.Fragment>
      {props.actionAreaType === "comment" &&
        !props.isHiddenStreamingComment && (
          <StreamingActionAreaLayout
            className={"streaming_action_area_height"}
            header={streamingActionHeaderNoClose}
          >
            {streamingAnnouncement}
            {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

            <Box flex={"1 1 auto"}>{streamingCommentV2}</Box>
            {streamingSelectGiftV2}
            {streamingSendCommentV2}
          </StreamingActionAreaLayout>
        )}

      {props.actionAreaType === "goods" && props.isLiveCommerceEnabled && (
        <StreamingActionAreaLayout
          className={"streaming_action_area_height"}
          header={streamingActionHeader}
        >
          {streamingAnnouncement}
          {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

          <StreamingLiveCommerceV2
            currentVideoId={props.currentVideoId}
            THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          />
        </StreamingActionAreaLayout>
      )}

      {props.isHiddenStreamingComment && (
        <StreamingCommentHidden
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
        />
      )}
    </React.Fragment>
    // </Box>
  );
};
export default StreamingActionAreaV2;

const StreamingActionAreaLayout: FC<{
  children: React.ReactNode;
  header: React.ReactNode;
  className: string;
}> = ({ children, header, className }) => {
  const popOutMode = isPopOut();
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  return (
    <Box
      className={`streaming_action_area ${className}`}
      display={"flex"}
      flexDirection={"column"}
      gridGap={"12px"}
      padding={"12px"}
      {...(popOutMode && {
        height: "100vh",
        marginX: "auto",
        bgcolor: isDarkModeEnabled ? "#202020" : "#fff",
      })}
    >
      {header}
      {children}
    </Box>
  );
};
